import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, describeAxiosError } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true });

export const useAuth = () => useContext(AuthContext);

const isDev = process.env.NODE_ENV !== "production";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we're returning from Google OAuth, AuthCallback will exchange the session_id
    // and set the JWT cookies. Skip the /auth/me probe to avoid a 401 race.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: describeAxiosError(e) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: describeAxiosError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn("Logout API failed (clearing local state anyway):", err);
      }
    }
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render on unrelated parent updates.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
